import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * usePushNotifications — encapsulates the Web Push subscription lifecycle.
 *
 *  - `supported`     : browser supports SW + PushManager + Notification API
 *  - `permission`    : 'default' | 'granted' | 'denied'
 *  - `subscribed`    : already has an active push subscription on this device
 *  - `enable()`      : asks permission, subscribes, registers with backend
 *  - `disable()`     : unsubscribes locally + deletes from backend
 */
export default function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    }).catch(() => {});
  }, []);

  const enable = useCallback(async () => {
    if (!supported) return false;
    setBusy(true);
    try {
      // 1. Ask permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return false;

      // 2. Register the SW (idempotent)
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;

      // 3. Get VAPID public key from backend
      const { data } = await api.get("/push/vapid-public-key");
      const publicKey = data?.key;
      if (!publicKey) throw new Error("VAPID public key not configured");

      // 4. Subscribe (idempotent — returns existing or creates new)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      // 5. Tell backend
      await api.post("/push/subscribe", sub.toJSON());
      setSubscribed(true);
      return true;
    } catch (e) {
      console.error("[push] enable failed:", e);
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post("/push/test");
      return data?.sent || 0;
    } catch {
      return 0;
    }
  }, []);

  return { supported, permission, subscribed, busy, enable, disable, sendTest };
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}
